import { useState, useEffect, useRef } from 'react'
import { Outlet } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { WifiOff, Loader2, Check } from 'lucide-react'
import { Sidebar } from './Sidebar'
import { MobileNav } from './MobileNav'
import { Navbar } from './Navbar'
import { useNetworkStatus } from '@/hooks/useNetworkStatus'
import { useSyncStatus } from '@/hooks/useSyncStatus'

export function AppLayout() {
  const { isOnline } = useNetworkStatus()
  const { hasPendingWrites } = useSyncStatus()
  const [showSaved, setShowSaved] = useState(false)
  const prevPendingRef = useRef(false)

  // When pending writes flush to the server, briefly show "All changes saved"
  useEffect(() => {
    if (prevPendingRef.current && !hasPendingWrites && isOnline) {
      setShowSaved(true)
      const t = setTimeout(() => setShowSaved(false), 2000)
      return () => clearTimeout(t)
    }
    prevPendingRef.current = hasPendingWrites
  }, [hasPendingWrites, isOnline])

  // Derive which status banner to show (mutually exclusive)
  const showOffline = !isOnline
  const showSyncing = isOnline && hasPendingWrites
  const showAllSaved = isOnline && !hasPendingWrites && showSaved

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Navbar />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 pb-20 md:pb-6">
          <Outlet />
        </main>
      </div>
      <MobileNav />

      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-2 pointer-events-none">
        <AnimatePresence initial={false}>
          {showOffline && (
            <motion.div
              key="offline"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 backdrop-blur-sm px-3 py-2 text-sm text-amber-600 dark:text-amber-400 shadow-sm whitespace-nowrap">
                <WifiOff className="h-4 w-4 shrink-0" />
                <span>You're offline — changes will sync automatically when you reconnect</span>
              </div>
            </motion.div>
          )}

          {showSyncing && (
            <motion.div
              key="syncing"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex items-center gap-2 rounded-lg border border-blue-500/30 bg-blue-500/10 backdrop-blur-sm px-3 py-2 text-sm text-blue-600 dark:text-blue-400 shadow-sm whitespace-nowrap">
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                <span>Syncing changes...</span>
              </div>
            </motion.div>
          )}

          {showAllSaved && (
            <motion.div
              key="saved"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex items-center gap-2 rounded-lg border border-green-500/30 bg-green-500/10 backdrop-blur-sm px-3 py-2 text-sm text-green-600 dark:text-green-400 shadow-sm whitespace-nowrap">
                <Check className="h-4 w-4 shrink-0" />
                <span>All changes saved</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
